import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  InfiniteScroll,
  LoadingSpinner,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  TruncatedText,
  calculateSelectPopoverHeight,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useInfiniteSelectShapes } from '../../Shapes/hooks/useInfiniteSelectShapes';
import { ShapeSelectType, formatCirclePosition } from '../../Shapes/utils';

export interface CirclePositionSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  className?: string;
}

export function CirclePositionSelect({
  value,
  onChange,
  className,
}: CirclePositionSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { messages } = useLocalization();
  const { filteredFlattenedShapes, isLoading, flattenedShapes } =
    useInfiniteSelectShapes(search, ['Circle']);

  // Find which circle shape matches the current value (if any)
  const matchedShape = useMemo(() => {
    if (!value) return undefined;
    return flattenedShapes?.find(
      (shape) =>
        formatCirclePosition(shape.coordinates, shape.radius) === value,
    );
  }, [flattenedShapes, value]);

  const calculatedHeight = useMemo(
    () =>
      calculateSelectPopoverHeight({
        itemCount: filteredFlattenedShapes.length,
        isLoading,
      }),
    [filteredFlattenedShapes.length, isLoading],
  );

  return (
    <div className={cn('flex gap-1 items-center w-full p-px', className)}>
      <div className="flex-1 min-w-0">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          placeholder="(lat, lon, radius) or select circle →"
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          autoComplete="off"
        />
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-9 w-9 p-0 shrink-0"
            title={messages.shapeSelectText || 'Select circle'}
          >
            <ChevronsUpDown className="size-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContentNoPortal
          align="end"
          className="w-[280px] p-0 overflow-hidden"
          style={{ height: `${calculatedHeight}px` }}
        >
          <Command shouldFilter={false} className="flex flex-col h-full">
            <CommandInput
              placeholder={messages.shapeSelectSearchText}
              onValueChange={setSearch}
              value={search}
            />
            {!isLoading && (
              <CommandEmpty>
                {messages.shapeSelectNoResultsCircles || 'No circles found.'}
              </CommandEmpty>
            )}
            {isLoading && <LoadingSpinner size="small" />}
            {!isLoading && flattenedShapes.length > 0 && (
              <InfiniteScroll
                items={filteredFlattenedShapes}
                limit={30}
                observeResize={true}
                renderItem={(shapeSelect: ShapeSelectType) => {
                  const circleValue = formatCirclePosition(
                    shapeSelect.coordinates,
                    shapeSelect.radius,
                  );
                  return (
                    <CommandItem
                      key={shapeSelect.id}
                      disabled={
                        shapeSelect.type === 'FeatureCollection' || !circleValue
                      }
                      value={shapeSelect.id}
                      onSelect={() => {
                        if (circleValue) {
                          onChange(
                            circleValue === value ? undefined : circleValue,
                          );
                        }
                        setOpen(false);
                      }}
                    >
                      <div className="flex justify-between w-full">
                        <div className="flex min-w-0 max-w-[85%]">
                          <span>
                            {new Array(shapeSelect.level + 1).join(
                              '\u00A0\u00A0\u00A0',
                            )}
                          </span>
                          <div className="px-2 flex-shrink-0">
                            <shapeSelect.icon />
                          </div>
                          <TruncatedText
                            text={shapeSelect?.name ? shapeSelect.name : ''}
                            maxWidth="250px"
                          />
                        </div>
                        <Check
                          className={cn(
                            'mr-1 size-4 flex-shrink-0',
                            matchedShape?.id === shapeSelect.id
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                      </div>
                    </CommandItem>
                  );
                }}
                className="flex-1 min-h-0 p-0 border rounded-lg"
              />
            )}
          </Command>
        </PopoverContentNoPortal>
      </Popover>
    </div>
  );
}
